import { useTranslation } from 'react-i18next';
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router';

import { useBoards } from '@/entities/board';
import { useViewer } from '@/entities/viewer';
import { canCreateTasks, useWorkspace } from '@/entities/workspace';
import { CalendarView } from '@/features/calendar';
import { ErrorState, LoadingState } from '@/shared/ui';

import styles from './WorkspaceCalendarPage.module.css';

/** `/app/workspaces/:workspaceId/calendar`: all (chosen) boards of the workspace on one calendar. */
export function WorkspaceCalendarPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { workspaceId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const viewer = useViewer();
  const workspace = useWorkspace(workspaceId);
  const boards = useBoards(workspaceId);
  const user = viewer.status === 'authenticated' ? viewer.user : null;

  const back = (
    <Link to={`/app/workspaces/${workspaceId}`} className={styles.back}>
      ← {t('calendar.back')}
    </Link>
  );
  if (workspace.isPending || boards.isPending) return <LoadingState />;
  if (workspace.isError) return <ErrorState error={workspace.error} />;
  if (boards.isError)
    return <ErrorState error={boards.error} onRetry={() => void boards.refetch()} />;

  const canEdit = canCreateTasks(workspace.data);
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        {back}
        <h1 className={styles.title}>{t('calendar.pageTitle')}</h1>
        <span className={styles.workspace}>{workspace.data.name}</span>
      </header>
      <CalendarView
        scope={{ kind: 'workspace', workspaceId }}
        boards={boards.data}
        canEdit={canEdit}
        firstDay={user?.first_day_of_week ?? 0}
        timeZone={user?.timezone ?? null}
        locale={i18n.language}
        onOpenCard={(cardId) =>
          void navigate({ pathname: `cards/${cardId}`, search: searchParams.toString() })
        }
      />
      <Outlet />
    </div>
  );
}
